import { getFirestore } from 'firebase/firestore';
import { doc, getDoc } from 'firebase/firestore';
import { CompanySettings, Donation } from '@/types/firebase';
import { sendDonationThankYouEmail, sendInvoiceEmail } from './email';
import { defaultTemplates } from './emailTemplates';

interface CardOnFileTransactionEmailData {
  transactionId: string;
  amount: number;
  status: 'approved' | 'declined';
  authCode?: string;
  cardLast4?: string;
  responseText?: string;
  customerEmail: string;
  customerName: string;
  companyId: string;
  invoiceNumber?: string;
  invoiceId?: string;
}

/**
 * Sends an email notification to the customer about a card-on-file transaction
 */
export async function sendCardOnFileTransactionEmail(data: CardOnFileTransactionEmailData): Promise<boolean> {
  try {
    // Guardrail: never send generic payment emails for donations
    if ((data.invoiceId && (data.invoiceId.startsWith('donation-') || data.invoiceId.startsWith('DON-'))) ||
        (data.invoiceNumber && String(data.invoiceNumber).startsWith('DON-'))) {
      console.warn('[emailNotifications] Skipping generic payment email for donation context', {
        invoiceId: data.invoiceId,
        invoiceNumber: data.invoiceNumber
      });
      return false;
    }

    // Get company settings to check if email notifications are enabled
    const db = getFirestore();
    // Prefer companies collection (primary source). Fallback to legacy settings collection
    let companySettingsDoc = await getDoc(doc(db, 'companies', data.companyId));
    if (!companySettingsDoc.exists()) {
      companySettingsDoc = await getDoc(doc(db, 'settings', data.companyId));
    }
    if (!companySettingsDoc.exists()) {
      console.error('Company settings not found for email notification');
      return false;
    }
    const companySettings = companySettingsDoc.data() as CompanySettings;
    
    // Check if SendGrid API key is set (either in company settings or environment)
    const companySendgridApiKey = companySettings.emailSettings?.sendgridApiKey;
    const fallbackSendgridApiKey = process.env.SENDGRID_API_KEY;
    
    if (!companySendgridApiKey && !fallbackSendgridApiKey) {
      console.log('SendGrid API key is not set in company settings or environment variables');
      return false;
    }
    
    // Log which API key source will be used
    if (companySendgridApiKey) {
      console.log('Using SendGrid API key from company settings for email notification');
    } else {
      console.log('Using fallback SendGrid API key from environment variables for email notification');
    }
    
    // Select template + subject based on transaction status
    const template = data.status === 'approved'
      ? companySettings.emailSettings.paymentThankYou
      : companySettings.emailSettings.paymentFailed;
    const subject = data.status === 'approved'
      ? (companySettings.emailSettings.paymentThankYouSubject || 'Payment Received - Thank You')
      : (companySettings.emailSettings.paymentFailedSubject || 'Payment Failed');

    if (!template || !subject) {
      console.error(`Email template/subject not configured for ${data.status} transactions`);
      return false;
    }

    // Create a minimal virtual invoice for the email API
    const virtualInvoice: any = {
      id: data.invoiceId || `txn-${data.transactionId}`,
      invoiceNumber: String(data.invoiceNumber || ''),
      customerName: data.customerName,
      customerEmail: data.customerEmail,
      total: data.amount,
      status: data.status === 'approved' ? 'paid' : 'pending',
      companyId: data.companyId,
      date: new Date().toISOString().split('T')[0],
      // Optional metadata for template variables
      metadata: {
        transactionId: data.transactionId,
        authCode: data.authCode,
        responseText: data.responseText,
        cardLast4: data.cardLast4,
      },
    };

    // Use the centralized email sender (targets backend API at getApiBaseUrl()/api/email)
    await sendInvoiceEmail({
      invoice: virtualInvoice,
      template,
      subject,
      isCustomContent: false,
      isDonation: false,
    });

    console.log(`Card-on-file transaction email sent to ${data.customerEmail} via /api/email (${data.status})`);
    return true;
  } catch (error) {
    console.error('Error sending card-on-file transaction email:', error);
    return false;
  }
}

/**
 * Sends a thank you email after a successful donation
 * 
 * @param donation The donation that was completed
 * @returns A boolean indicating if the email was sent successfully
 */
export async function sendDonationThankYouNotification(donation: Donation): Promise<boolean> {
  try {
    // Skip if no email address
    if (!donation.email) {
      console.log('No email address provided for donation thank you notification');
      return false;
    }
    
    // Get company settings
    const db = getFirestore();
    const companySettingsDoc = await getDoc(doc(db, 'companies', donation.companyId));
    
    if (!companySettingsDoc.exists()) {
      console.error('Company settings not found for donation thank you notification');
      return false;
    }
    
    const companySettings = companySettingsDoc.data() as CompanySettings;
    
    // Check if SendGrid API key is set (either in company settings or environment)
    const companySendgridApiKey = companySettings.emailSettings?.sendgridApiKey;
    const fallbackSendgridApiKey = process.env.SENDGRID_API_KEY;
    
    if (!companySendgridApiKey && !fallbackSendgridApiKey) {
      console.log('SendGrid API key is not set in company settings or environment variables for donation email');
      return false;
    }
    
    // Log which API key source will be used
    if (companySendgridApiKey) {
      console.log('Using SendGrid API key from company settings for donation thank you email');
    } else {
      console.log('Using fallback SendGrid API key from environment variables for donation thank you email');
    }
    
    // Check if donation thank you emails are enabled
    if (!companySettings.emailSettings?.enableDonationThankYouEmail) {
      console.log('Donation thank you emails are disabled');
      return false;
    }
    
    // Get the donation thank you email template
    const template = companySettings.emailSettings?.donationThankYou || defaultTemplates.donationThankYou;
    const subject = companySettings.emailSettings?.donationThankYouSubject || defaultTemplates.donationThankYouSubject;
    
    // Send the thank you email
    await sendDonationThankYouEmail({
      donation,
      template,
      subject
    });
    
    console.log(`Donation thank you email sent to ${donation.email}`);
    return true;
  } catch (error) {
    console.error('Error sending donation thank you email:', error);
    return false;
  }
} 
